import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useOutletContext } from "react-router-dom";
import {
  type CrawlBundle,
  type CrawlCategoryUniverse,
  type CrawlPage,
  getCrawl,
  runCrawl,
  saveCrawlAssignments,
  uploadCrawlCsv,
} from "../lib/api";
import { Button, Field, TextInput } from "../components/Form";
import type { WorkspaceContext } from "./ClientWorkspacePage";

/**
 * "Main" page classifier (chunk3z-fix94).
 *
 * Main pages = the home page, plus any page UNDER /services/ (a child like
 * /services/teeth-whitening/). The /services parent index itself is NOT a main
 * page — it's a hub, not a service page. Everything else is "Other".
 *
 * Used to:
 *   - split the crawl results into Main vs Other sections
 *   - decide which rows get a Service/Category dropdown (Main only)
 *   - scope the "No H1" critical list to home + service pages
 */
export function isMainPage(path: string): boolean {
  if (path === "/" || path === "") return true;
  const trimmed = (path || "").toLowerCase().replace(/\/+$/, "");
  if (trimmed === "/services") return false; // the parent index is not a main page
  return trimmed.startsWith("/services/");
}


/**
 * Site Crawler tab.
 *
 * Workflow:
 *   1. User crawls (or uploads a Screaming Frog CSV) for the client's website.
 *   2. Each page gets auto-classified to one of the client's GBP
 *      categories or services. The user can override per-row.
 *   3. Assignment changes save automatically on selection.
 *
 * The classifications are the input to the gap analysis (which lives in the
 * Research tab — built in a later chunk) and the "Import Missing Core 30"
 * button on the Build Queue tab.
 */
export default function CrawlTab() {
  const { client } = useOutletContext<WorkspaceContext>();
  const [bundle, setBundle] = useState<CrawlBundle | null>(null);
  const [websiteUrl, setWebsiteUrl] = useState(client.websiteUrl);
  const [crawling, setCrawling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Auto-save: each dropdown change is persisted immediately. We keep a small
  // set of in-flight page ids so the row can show a subtle "saving" cue, plus
  // a transient set of just-saved ids for a brief "saved" tick. No buffer, no
  // Save button — the selection IS the saved state.
  const [savingIds, setSavingIds] = useState<Set<number>>(new Set());
  const [savedIds, setSavedIds] = useState<Set<number>>(new Set());

  async function refresh() {
    try {
      const data = await getCrawl(client.id);
      setBundle(data);
    } catch (err) {
      console.error("Crawl load failed:", err);
    }
  }

  useEffect(() => {
    setWebsiteUrl(client.websiteUrl);
    refresh().catch(console.error);
  }, [client.id, client.websiteUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleCrawl() {
    setError(null);
    setInfo(null);
    setCrawling(true);
    try {
      const result = await runCrawl(client.id, { websiteUrl });
      await refresh();
      if (result.stubbed && result.pageCount === 1) {
        setInfo(
          "Crawler is stubbed for this client right now. CSV upload from Screaming Frog gives you the full picture in the meantime."
        );
      } else {
        setInfo(`Crawl complete — ${result.pageCount} pages.`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Crawl failed.");
    } finally {
      setCrawling(false);
    }
  }

  async function handleCsvUpload(file: File) {
    setError(null);
    setInfo(null);
    setCrawling(true);
    try {
      const csv = await file.text();
      const result = await uploadCrawlCsv(client.id, csv);
      await refresh();
      setInfo(`CSV imported — ${result.pageCount} pages.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "CSV upload failed.");
    } finally {
      setCrawling(false);
    }
  }

  // Auto-save a single row's assignment the moment it changes. Picking the same
  // value as the auto-classification persists it as "no override" (null) so we
  // don't store redundant overrides.
  async function setRowAssignment(pageId: number, value: string) {
    const original = bundle?.pages.find((p) => p.id === pageId);
    if (!original) return;
    const auto = original.autoAssignment ?? "none";
    const manualAssignment = value === auto ? null : value;

    // Optimistically reflect the change in local bundle state so the dropdown
    // updates instantly, then persist.
    setBundle((prev) =>
      prev
        ? {
            ...prev,
            pages: prev.pages.map((p) =>
              p.id === pageId ? { ...p, manualAssignment } : p
            ),
          }
        : prev
    );

    setSavingIds((s) => new Set(s).add(pageId));
    setError(null);
    try {
      await saveCrawlAssignments(client.id, [{ pageId, manualAssignment }]);
      // brief "saved" tick
      setSavedIds((s) => new Set(s).add(pageId));
      window.setTimeout(() => {
        setSavedIds((s) => {
          const next = new Set(s);
          next.delete(pageId);
          return next;
        });
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed.");
      // Roll back the optimistic change on failure.
      await refresh();
    } finally {
      setSavingIds((s) => {
        const next = new Set(s);
        next.delete(pageId);
        return next;
      });
    }
  }

  // Effective assignment for a page = saved manual OR auto.
  function effectiveAssignment(page: CrawlPage): string {
    return page.manualAssignment ?? page.autoAssignment ?? "none";
  }

  // Badge state for a row's classification dropdown:
  //   - "auto":      using auto + auto picked a high-confidence value (blue).
  //   - "check":     auto returned 'none' but the path is /services/<X>
  //                  (orange — review needed).
  //   - "missing":   auto returned 'none' AND the path is /services/<X>/<Y>
  //                  with a service term not in the GBP list (red).
  //   - "manual":    user has picked a value manually (no badge shown).
  //   - "none":      auto returned 'none' on a non-service path. Silent.
  //   - "duplicate": effective value is the dropdown value of 2+ rows
  //                  (yellow warning). Each service can only be the
  //                  assignment of ONE page.
  //
  // Backend persists `autoStatus` directly so we don't reverse-engineer
  // it from path patterns. Frontend trusts what backend says, and only
  // adds the "duplicate" overlay (which depends on the full corpus).
  function badgeState(
    page: CrawlPage
  ): "auto" | "check" | "missing" | "manual" | "none" | "duplicate" {
    // Compute effective value + whether it came from a manual choice.
    // Assignments save immediately into bundle state, so manual_assignment is
    // always the source of truth — no edit buffer.
    let effective: string;
    let isManual: boolean;
    if (page.manualAssignment) {
      effective = page.manualAssignment;
      isManual = true;
    } else {
      effective = page.autoAssignment ?? "none";
      isManual = false;
    }

    // Duplicate check (any non-{none, homepage} value used by 2+ rows)
    // takes precedence over manual/auto status — duplicates need attention.
    if (
      effective !== "none" &&
      effective !== "homepage" &&
      duplicateValues.has(effective)
    ) {
      return "duplicate";
    }

    if (isManual) return "manual";
    // Auto path — use the persisted status from the backend.
    if (page.autoStatus === "auto") return "auto";
    if (page.autoStatus === "check") return "check";
    if (page.autoStatus === "missing") return "missing";
    if (page.autoStatus === "homepage") return "auto";
    return "none";
  }

  // Set of dropdown values currently used by 2+ rows. Recomputed when bundle
  // changes. Drives the "duplicate" badge state.
  const duplicateValues = useMemo(() => {
    if (!bundle) return new Set<string>();
    const counts = new Map<string, number>();
    for (const p of bundle.pages) {
      const v = p.manualAssignment ?? p.autoAssignment ?? "none";
      if (v === "none" || v === "homepage") continue;
      counts.set(v, (counts.get(v) ?? 0) + 1);
    }
    const dups = new Set<string>();
    for (const [v, n] of counts) if (n >= 2) dups.add(v);
    return dups;
  }, [bundle]);

  // Rows using auto (any confidence) count toward "Auto-assigned: N".
  function isAuto(page: CrawlPage): boolean {
    const s = badgeState(page);
    return s === "auto" || s === "check" || s === "missing";
  }

  const autoAssignedCount = useMemo(() => {
    if (!bundle) return 0;
    return bundle.pages.filter((p) => badgeState(p) === "auto").length;
  }, [bundle, duplicateValues]); // eslint-disable-line react-hooks/exhaustive-deps

  const needsCheckCount = useMemo(() => {
    if (!bundle) return 0;
    return bundle.pages.filter((p) => badgeState(p) === "check").length;
  }, [bundle, duplicateValues]); // eslint-disable-line react-hooks/exhaustive-deps

  const missingCount = useMemo(() => {
    if (!bundle) return 0;
    return bundle.pages.filter((p) => badgeState(p) === "missing").length;
  }, [bundle, duplicateValues]); // eslint-disable-line react-hooks/exhaustive-deps

  const duplicateCount = useMemo(() => {
    if (!bundle) return 0;
    return bundle.pages.filter((p) => badgeState(p) === "duplicate").length;
  }, [bundle, duplicateValues]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4">
      {/* Header — white card, matching Gap Analysis / Supporting Pages */}
      <div className="bg-bg-card border border-border rounded-lg p-5 shadow-card flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-bg-subtle border border-border flex items-center justify-center text-text-muted shrink-0">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="2" y1="12" x2="22" y2="12" />
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
          </svg>
        </div>
        <div>
          <h1 className="text-text font-semibold text-xl">Site Crawler</h1>
          <p className="text-text-muted text-sm mt-0.5">
            Crawl the client website to extract page data, headings, and
            technical info.
          </p>
        </div>
      </div>

      {/* Crawl form card */}
      <CrawlFormCard
        websiteUrl={websiteUrl}
        onWebsiteChange={setWebsiteUrl}
        onCrawl={handleCrawl}
        onCsvUpload={handleCsvUpload}
        crawling={crawling}
        hasExistingCrawl={Boolean(bundle?.lastCrawledAt)}
        gbpReady={
          // Crawler classifies pages against the client's GBP categories +
          // services (see top-of-file comment). Without those filled in, the
          // crawl runs but every page lands in "Unassigned" — wasted work.
          // Block the button until the user has at least Primary + Services.
          (client.gbpPrimaryCategory ?? "").trim().length > 0 &&
          client.gbpServices != null &&
          Object.keys(client.gbpServices).length > 0
        }
        error={error}
        info={info}
        statsSlot={
          bundle && bundle.pages.length > 0 ? (
            <StatsStrip
              stats={bundle.stats}
              pages={bundle.pages}
              lastCrawledAt={bundle.lastCrawledAt}
              source={bundle.lastCrawlSource}
            />
          ) : null
        }
      />

      {/* Results table */}
      {bundle && bundle.pages.length > 0 && (
        <CrawlResultsTable
          pages={bundle.pages}
          universe={bundle.universe}
          getEffectiveAssignment={effectiveAssignment}
          badgeStateForRow={badgeState}
          onChangeAssignment={setRowAssignment}
          autoAssignedCount={autoAssignedCount}
          needsCheckCount={needsCheckCount}
          missingCount={missingCount}
          duplicateCount={duplicateCount}
          savingIds={savingIds}
          savedIds={savedIds}
        />
      )}

      {bundle && bundle.pages.length === 0 && !crawling && (
        <div className="bg-bg-card border border-border rounded-lg p-8 text-center shadow-card">
          <p className="text-text-muted text-sm">
            No crawl data yet. Click Crawl above, or upload a Screaming Frog CSV.
          </p>
        </div>
      )}
    </div>
  );
}

// =========================================================================
// Card: crawl form
// =========================================================================
function CrawlFormCard({
  websiteUrl,
  onWebsiteChange,
  onCrawl,
  onCsvUpload,
  crawling,
  hasExistingCrawl,
  gbpReady,
  error,
  info,
  statsSlot,
}: {
  websiteUrl: string;
  onWebsiteChange: (v: string) => void;
  onCrawl: () => void;
  onCsvUpload: (file: File) => void;
  crawling: boolean;
  hasExistingCrawl: boolean;
  gbpReady: boolean;
  error: string | null;
  info: string | null;
  /** Optional stats panel rendered INSIDE the same card as the form, so the
   *  whole Site Crawler card (form + stats + No-H1 detail panel) reads as
   *  one unified block. Stats are passed as a slot rather than coupled into
   *  this component to keep the form-only concerns clean. */
  statsSlot?: ReactNode;
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  return (
    <div className="bg-bg-card border border-border rounded-lg p-5 shadow-card">
      {!gbpReady && (
        <div className="bg-warning-bg border border-warning-border rounded-md px-3 py-2.5 mb-4 text-sm">
          <div className="flex items-start gap-2">
            <span className="text-warning text-base mt-0.5">⚠</span>
            <div className="flex-1">
              <p className="font-medium text-text">
                Fill GBP Categories & Services first.
              </p>
              <p className="text-text-muted mt-0.5">
                The crawler classifies each page against the client's GBP
                categories and services. Without them, every page lands in
                "Unassigned" — wasted work. Go to{" "}
                <span className="font-medium">Client Settings → Business Location & GBP</span>{" "}
                and set the Primary Category and Services (paste from the GBP
                dashboard or use Auto-Fill).
              </p>
            </div>
          </div>
        </div>
      )}

      <Field label="Website URL">
        <TextInput
          value={websiteUrl}
          onChange={onWebsiteChange}
          placeholder="https://example.com/"
        />
      </Field>

      <div className="flex items-center gap-2 mt-2">
        <Button
          variant="primary"
          onClick={onCrawl}
          disabled={crawling || !websiteUrl.trim() || !gbpReady}
        >
          {crawling ? "↻ Working..." : hasExistingCrawl ? "🔄 Re-Crawl" : "🌐 Crawl"}
        </Button>
        <Button
          variant="secondary"
          onClick={() => fileInputRef.current?.click()}
          disabled={crawling || !gbpReady}
        >
          ⬆ Upload CSV
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onCsvUpload(f);
            e.target.value = ""; // allow re-uploading the same file
          }}
        />
      </div>
      <p className="text-text-subtle text-xs mt-2">
        Crawl not working? Export <span className="text-text-muted">Internal &gt; All</span> from
        Screaming Frog as CSV and upload it instead.
      </p>

      {error && (
        <div className="text-sm text-danger bg-danger-bg border border-danger-border rounded-md px-3 py-2 mt-3">
          {error}
        </div>
      )}
      {info && !error && (
        <div className="text-sm text-text bg-accent-subtle border border-accent/30 rounded-md px-3 py-2 mt-3">
          {info}
        </div>
      )}

      {/* Stats + No-H1 detail panel rendered inside the Site Crawler card
          so the whole block (form, helper text, stats, drill-down) reads
          as one unified container. Hidden until a crawl has run. */}
      {statsSlot && <div className="mt-5">{statsSlot}</div>}
    </div>
  );
}

// =========================================================================
// Stats strip
// =========================================================================
function StatsStrip({
  stats,
  pages,
  lastCrawledAt,
  source,
}: {
  stats: CrawlBundle["stats"];
  pages: CrawlPage[];
  lastCrawledAt: string | null;
  source: string | null;
}) {
  // Click-to-expand panel for the No H1 stat. Defaults closed; clicking
  // the tile toggles. Only one panel for now (No H1 is the only actionable
  // diagnostic), but the pattern is extensible — future warning stats
  // could share the same `expandedStat` state.
  const [expandedStat, setExpandedStat] = useState<"noH1" | null>(null);

  // Split No-H1 pages into "critical" and "other" buckets. Critical = pages
  // where missing an H1 is an actual SEO problem:
  //   - homepage ('/')
  //   - /services and /services/* (the pages we actually want to rank for)
  // Other = blog posts, taxonomy archives, contact, before-after, etc. —
  // pages where missing an H1 is usually fine.
  //
  // Same eligibility rule used by the auto-classifier (see
  // crawler.ts isServicesPath). When/if we tweak the rule there, mirror
  // it here so the two stay in sync.
  const noH1Pages = pages.filter((p) => !p.hasH1);
  const isCriticalPath = (path: string): boolean => isMainPage(path);
  const noH1Critical = noH1Pages.filter((p) => isCriticalPath(p.path));
  const noH1Other = noH1Pages.filter((p) => !isCriticalPath(p.path));

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Total Pages" value={stats.totalPages.toLocaleString()} icon="📄" />
        <Stat label="Avg Words" value={stats.avgWords.toLocaleString()} icon="📝" />
        <Stat
          label="No H1"
          value={noH1Critical.length.toLocaleString()}
          subValue={
            noH1Other.length > 0 ? `Other: ${noH1Other.length}` : undefined
          }
          icon="⚠"
          // Main number = CRITICAL no-H1 (homepage/service pages) — that's the
          // actionable count. Non-critical no-H1 (blog posts etc.) show as a
          // small grey "Other: N" subline. Tile lights up warning only when a
          // critical page is missing its H1.
          tone={noH1Critical.length > 0 ? "warning" : "default"}
          onClick={
            stats.noH1Count > 0
              ? () =>
                  setExpandedStat((prev) => (prev === "noH1" ? null : "noH1"))
              : undefined
          }
          isExpanded={expandedStat === "noH1"}
        />
        <Stat
          label="Internal Links"
          value={stats.totalInternalLinks.toLocaleString()}
          icon="🔗"
        />
        <Stat
          label="External Links"
          value={stats.totalExternalLinks.toLocaleString()}
          icon="🔗"
        />
      </div>
      {lastCrawledAt && (
        <p className="text-text-subtle text-xs mt-2">
          Last crawl {formatTimestamp(lastCrawledAt)}
          {source ? ` · ${sourceLabel(source)}` : ""} · {stats.totalPages} pages
        </p>
      )}

      {/* No H1 detail panel — opens inline below the stat strip when the
          No H1 tile is clicked. Splits the affected pages into:
            - Critical: homepage + /services pages (real SEO problem)
            - Other: blog posts, archives, etc. (usually not a problem)
          The Critical section gets a strong red header. The Other section
          is rendered below in a more muted style. */}
      {expandedStat === "noH1" && (
        <div className="mt-3 bg-bg-card border border-accent/40 rounded-lg shadow-card overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
            <p className="text-accent font-semibold text-sm">
              Pages Without H1 ({noH1Pages.length})
            </p>
            <button
              type="button"
              onClick={() => setExpandedStat(null)}
              className="text-text-muted hover:text-text transition text-lg leading-none"
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {/* Critical section — homepage + service pages. Always rendered
                (with a "none" placeholder) so the user immediately sees
                whether they have any critical issues. */}
            <NoH1Section
              title="Critical — Homepage & Service Pages"
              subtitle="These pages need an H1 for SEO. Fix these first."
              pages={noH1Critical}
              tone="critical"
              emptyText="No critical pages missing H1. ✓"
            />

            {/* Other section — only renders when there are entries. The
                "no big deal" framing makes it clear these aren't urgent. */}
            {noH1Other.length > 0 && (
              <div className="mt-2 border-t border-border">
                <NoH1Section
                  title={`Other Pages (${noH1Other.length})`}
                  subtitle="Blog posts, archives, etc. — missing H1 here is usually fine."
                  pages={noH1Other}
                  tone="muted"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// =========================================================================
// No-H1 panel section (Critical / Other variants)
// =========================================================================
function NoH1Section({
  title,
  subtitle,
  pages,
  tone,
  emptyText,
}: {
  title: string;
  subtitle: string;
  pages: CrawlPage[];
  tone: "critical" | "muted";
  /** Placeholder rendered when pages.length === 0. Only used by the
   *  Critical section — Other section just doesn't render when empty. */
  emptyText?: string;
}) {
  // Only paint the header red when the critical section actually has pages.
  // An empty critical section is GOOD news, so it falls through to the neutral
  // "all clear" treatment instead of an alarming red.
  const isLoud = tone === "critical" && pages.length > 0;

  return (
    <div>
      {/* Section header */}
      <div className="px-4 py-2.5 bg-bg-subtle border-b border-border-subtle flex items-center gap-2">
        {isLoud ? (
          <span className="text-danger text-sm">⚠</span>
        ) : tone === "critical" ? (
          <span className="text-success text-sm">✓</span>
        ) : null}
        <div>
          <p
            className={`text-xs font-semibold uppercase tracking-wider ${
              isLoud ? "text-danger" : "text-text-muted"
            }`}
          >
            {title}
          </p>
          <p className="text-[11px] mt-0.5 text-text-subtle">{subtitle}</p>
        </div>
      </div>

      {/* Body: empty placeholder or table */}
      {pages.length === 0 ? (
        <div className="px-4 py-3 text-sm text-success">
          {emptyText ?? "None."}
        </div>
      ) : (
        <table className="w-full text-sm table-fixed">
          <colgroup>
            <col style={{ width: "40%" }} />
            <col />
            <col style={{ width: "84px" }} />
          </colgroup>
          <tbody>
            {pages.map((p) => (
              <tr
                key={p.id}
                className="border-t border-border-subtle hover:bg-bg-hover/40"
              >
                <td className="px-4 py-2">
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:underline font-mono text-xs truncate block"
                    title={p.path || "/"}
                  >
                    {p.path || "/"}
                  </a>
                </td>
                <td
                  className="px-4 py-2 text-text truncate"
                  title={p.title ?? ""}
                >
                  {p.title ?? <span className="text-text-subtle">—</span>}
                </td>
                <td className="px-4 py-2 text-right">
                  <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-danger-bg text-danger border border-danger-border">
                    Missing
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  subValue,
  icon,
  tone = "default",
  onClick,
  isExpanded,
}: {
  label: string;
  value: string;
  /** Optional small grey line under the main value, e.g. "Other: 5". */
  subValue?: string;
  icon: string;
  tone?: "default" | "warning";
  /** When provided, the tile becomes a button (cursor-pointer + click handler). */
  onClick?: () => void;
  /** When true, the tile gets a highlighted ring to show its detail panel
   *  is currently open. */
  isExpanded?: boolean;
}) {
  const wrap =
    tone === "warning"
      ? "bg-warning-bg border-warning-border"
      : "bg-bg-card border-border";
  const labelClass = tone === "warning" ? "text-warning" : "text-text-muted";
  const interactive = onClick
    ? "cursor-pointer hover:border-accent/60 transition"
    : "";
  const expandedRing = isExpanded ? "ring-2 ring-accent" : "";
  const content = (
    <>
      <div className={`flex items-center gap-2 text-xs uppercase tracking-wide ${labelClass}`}>
        <span>{icon}</span>
        <span>{label}</span>
      </div>
      <div className="text-text font-semibold text-lg mt-1">{value}</div>
      {subValue && (
        <div className="text-text-subtle text-xs mt-0.5">{subValue}</div>
      )}
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${wrap} ${interactive} ${expandedRing} border rounded-lg px-4 py-3 shadow-card text-left w-full`}
      >
        {content}
      </button>
    );
  }
  return (
    <div className={`${wrap} border rounded-lg px-4 py-3 shadow-card`}>
      {content}
    </div>
  );
}

function sourceLabel(s: string): string {
  if (s === "csv_upload") return "CSV upload";
  return "in-app crawl";
}

// =========================================================================
// Crawl results table
// =========================================================================
function CrawlResultsTable({
  pages,
  universe,
  getEffectiveAssignment,
  badgeStateForRow,
  onChangeAssignment,
  autoAssignedCount,
  needsCheckCount,
  missingCount,
  duplicateCount,
  savingIds,
  savedIds,
}: {
  pages: CrawlPage[];
  universe: CrawlCategoryUniverse;
  getEffectiveAssignment: (p: CrawlPage) => string;
  badgeStateForRow: (
    p: CrawlPage
  ) => "auto" | "check" | "missing" | "manual" | "none" | "duplicate";
  onChangeAssignment: (pageId: number, value: string) => void;
  autoAssignedCount: number;
  needsCheckCount: number;
  missingCount: number;
  duplicateCount: number;
  savingIds: Set<number>;
  savedIds: Set<number>;
}) {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Dropdown options:
  //   - "None" and "Homepage" pinned to the top (special values)
  //   - Everything else (primary GBP category, secondaries, services) sorted
  //     alphabetically. Per user request: alphabetical makes scanning a long
  //     dental services list dramatically easier than the previous
  //     primary-then-secondaries-then-services order.
  //   - Services that duplicate a category name are deduped (case-insensitive).
  const options = useMemo(() => {
    const sortable: { value: string; label: string }[] = [];
    if (universe.primary) {
      sortable.push({ value: universe.primary, label: universe.primary });
    }
    for (const s of universe.secondaries) {
      sortable.push({ value: s, label: s });
    }
    const seen = new Set(sortable.map((o) => o.value.toLowerCase()));
    for (const svc of universe.services) {
      if (!seen.has(svc.toLowerCase())) {
        sortable.push({ value: svc, label: svc });
        seen.add(svc.toLowerCase());
      }
    }
    sortable.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
    );
    return [
      { value: "none", label: "None" },
      { value: "skip", label: "Skip" },
      { value: "homepage", label: "Homepage" },
      ...sortable,
    ];
  }, [universe]);

  // chunk3z-fix502: split into Main (home + /services/ children), SKIP (pages
  // the user marked "Skip" — subpages that don't need their own category/service
  // tag because the parent already carries it), and Other. Skip is pulled out of
  // whichever bucket it would otherwise be in, so a skipped /services/ subpage
  // leaves Main and lands in Skip.
  const isSkipped = (p: CrawlPage) => getEffectiveAssignment(p) === "skip";
  const mainPages = useMemo(
    () => pages.filter((p) => isMainPage(p.path) && !isSkipped(p)),
    [pages, getEffectiveAssignment]
  );
  const skipPages = useMemo(
    () => pages.filter((p) => isSkipped(p)),
    [pages, getEffectiveAssignment]
  );
  const otherPages = useMemo(
    () => pages.filter((p) => !isMainPage(p.path) && !isSkipped(p)),
    [pages, getEffectiveAssignment]
  );


  return (
    <div>
      {/* Header strip */}
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-accent font-semibold">
          Crawl Results ({pages.length} pages)
        </h2>
        <span className="text-text-subtle text-xs">
          Changes save automatically
        </span>
      </div>

      {/* Main section — home + service pages, WITH Service/Category dropdown */}
      <div className="mb-5">
        <h3 className="text-text font-semibold text-sm mb-2">
          Main{" "}
          <span className="text-text-muted font-normal">
            ({mainPages.length}) — home page &amp; service pages
          </span>
        </h3>
        {mainPages.length === 0 ? (
          <div className="bg-bg-card border border-border rounded-lg p-4 text-center shadow-card">
            <p className="text-text-subtle text-sm">
              No home or /services/ pages found in this crawl.
            </p>
          </div>
        ) : (
          <div className="bg-bg-card border border-border rounded-lg shadow-card overflow-hidden">
            <table className="w-full text-sm table-fixed">
              <colgroup>
                <col style={{ width: "32px" }} />
                <col style={{ width: "38%" }} />
                <col />
                <col style={{ width: "64px" }} />
                <col style={{ width: "220px" }} />
              </colgroup>
              <thead className="bg-bg-subtle border-b border-border-subtle text-text-muted text-xs uppercase tracking-wide">
                <tr>
                  <th></th>
                  <th className="text-left px-3 py-2.5">URL</th>
                  <th className="text-left px-3 py-2.5">Title</th>
                  <th className="text-right px-3 py-2.5">Words</th>
                  <th className="text-left px-3 py-2.5">Service/Category</th>
                </tr>
              </thead>
              <tbody>
                {mainPages.map((p) => {
                  const expanded = expandedId === p.id;
                  return (
                    <CrawlRow
                      key={p.id}
                      page={p}
                      expanded={expanded}
                      onToggleExpand={() =>
                        setExpandedId(expanded ? null : p.id)
                      }
                      value={getEffectiveAssignment(p)}
                      badge={badgeStateForRow(p)}
                      options={options}
                      onChangeAssignment={(v) => onChangeAssignment(p.id, v)}
                      showAssignment
                      saving={savingIds.has(p.id)}
                      saved={savedIds.has(p.id)}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* chunk3z-fix502: Skip section — pages the user marked "Skip" (subpages
          that don't need their own category/service because the parent page is
          already tagged). Keeps the dropdown so the choice can be changed back. */}
      {skipPages.length > 0 && (
        <div className="mb-5">
          <h3 className="text-text font-semibold text-sm mb-2">
            Skip{" "}
            <span className="text-text-muted font-normal">
              ({skipPages.length}) — no category needed (parent already tagged)
            </span>
          </h3>
          <div className="bg-bg-card border border-border rounded-lg shadow-card overflow-hidden">
            <table className="w-full text-sm table-fixed">
              <colgroup>
                <col style={{ width: "32px" }} />
                <col style={{ width: "42%" }} />
                <col />
                <col style={{ width: "72px" }} />
                <col style={{ width: "220px" }} />
              </colgroup>
              <thead className="bg-bg-subtle border-b border-border-subtle text-text-muted text-xs uppercase tracking-wide">
                <tr>
                  <th></th>
                  <th className="text-left px-3 py-2.5">URL</th>
                  <th className="text-left px-3 py-2.5">Title</th>
                  <th className="text-right px-3 py-2.5">Words</th>
                  <th className="text-left px-3 py-2.5">Assignment</th>
                </tr>
              </thead>
              <tbody>
                {skipPages.map((p) => {
                  const expanded = expandedId === p.id;
                  return (
                    <CrawlRow
                      key={p.id}
                      page={p}
                      expanded={expanded}
                      onToggleExpand={() =>
                        setExpandedId(expanded ? null : p.id)
                      }
                      value={getEffectiveAssignment(p)}
                      badge={badgeStateForRow(p)}
                      options={options}
                      onChangeAssignment={(v) => onChangeAssignment(p.id, v)}
                      showAssignment={true}
                      saving={savingIds.has(p.id)}
                      saved={savedIds.has(p.id)}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Other Pages section — everything else, NO dropdown */}
      {otherPages.length > 0 && (
        <div>
          <h3 className="text-text font-semibold text-sm mb-2">
            Other Pages{" "}
            <span className="text-text-muted font-normal">
              ({otherPages.length})
            </span>
          </h3>
          <div className="bg-bg-card border border-border rounded-lg shadow-card overflow-hidden">
            <table className="w-full text-sm table-fixed">
              <colgroup>
                <col style={{ width: "32px" }} />
                <col style={{ width: "42%" }} />
                <col />
                <col style={{ width: "72px" }} />
              </colgroup>
              <thead className="bg-bg-subtle border-b border-border-subtle text-text-muted text-xs uppercase tracking-wide">
                <tr>
                  <th></th>
                  <th className="text-left px-3 py-2.5">URL</th>
                  <th className="text-left px-3 py-2.5">Title</th>
                  <th className="text-right px-3 py-2.5">Words</th>
                </tr>
              </thead>
              <tbody>
                {otherPages.map((p) => {
                  const expanded = expandedId === p.id;
                  return (
                    <CrawlRow
                      key={p.id}
                      page={p}
                      expanded={expanded}
                      onToggleExpand={() =>
                        setExpandedId(expanded ? null : p.id)
                      }
                      value={getEffectiveAssignment(p)}
                      badge={badgeStateForRow(p)}
                      options={options}
                      onChangeAssignment={(v) => onChangeAssignment(p.id, v)}
                      showAssignment={false}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function CrawlRow({
  page,
  expanded,
  onToggleExpand,
  value,
  badge,
  options,
  onChangeAssignment,
  showAssignment,
  saving = false,
  saved = false,
}: {
  page: CrawlPage;
  expanded: boolean;
  onToggleExpand: () => void;
  value: string;
  /** Badge state for this row's classification:
   *    "auto"      = blue ("auto") — high-confidence auto match
   *    "check"     = orange ("check") — under /services/X but no confident match
   *    "missing"   = red ("missing") — service not in client's GBP list
   *    "duplicate" = yellow ("duplicate") — value used by 2+ rows
   *    "manual"    = no badge (user assigned manually)
   *    "none"      = no badge (silent default for non-service paths) */
  badge: "auto" | "check" | "missing" | "manual" | "none" | "duplicate";
  options: { value: string; label: string }[];
  onChangeAssignment: (v: string) => void;
  /** Main pages get the Service/Category dropdown; Other Pages do not. */
  showAssignment: boolean;
  saving?: boolean;
  saved?: boolean;
}) {
  // colSpan for the expanded detail row: 3 base cols (URL/Title/Words) + the
  // assignment col when shown.
  const detailColSpan = showAssignment ? 4 : 3;
  return (
    <>
      <tr className="border-b border-border-subtle hover:bg-bg-hover/40">
        <td className="px-2 py-2">
          <button
            type="button"
            onClick={onToggleExpand}
            className="text-text-muted hover:text-text"
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            {expanded ? "▾" : "▸"}
          </button>
        </td>
        <td
          className="px-3 py-2 text-text-muted font-mono text-xs break-all align-top"
          title={page.url}
        >
          {page.path}
        </td>
        <td className="px-3 py-2 text-text truncate align-top" title={page.title ?? ""}>
          {page.title || <span className="text-text-subtle italic">(no title)</span>}
        </td>
        <td className="px-3 py-2 text-right text-accent align-top">
          {page.wordCount?.toLocaleString() ?? "—"}
        </td>
        {showAssignment && (
        <td className="px-3 py-2">
          <div className="flex items-center gap-1.5 min-w-0">
            {(() => {
              // A select whose value matches no option renders as the first
              // option ("None") in the browser, but `value` is still the stale
              // slug — so a literal `value === "none"` check missed it and the
              // row showed "None" WITHOUT the red border. Treat any value that
              // isn't a real option (or is literally "none") as effectively
              // None, so the red border is consistent across all "None" rows.
              const isEffectivelyNone =
                value === "none" || !options.some((o) => o.value === value);
              return (
                <select
                  value={isEffectivelyNone ? "none" : value}
                  onChange={(e) => onChangeAssignment(e.target.value)}
                  className={`flex-1 min-w-0 bg-bg-input border rounded px-2 py-1 text-xs text-text focus:outline-none focus:ring-2 ${
                    isEffectivelyNone
                      ? "border-danger ring-1 ring-danger/40 focus:ring-danger/30 focus:border-danger"
                      : "border-border focus:ring-accent/30 focus:border-accent"
                  }`}
                >
                  {options.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              );
            })()}
            {saving && (
              <span
                className="shrink-0 text-text-subtle inline-block animate-spin"
                title="Saving..."
              >
                ↻
              </span>
            )}
            {!saving && saved && (
              <span className="shrink-0 text-success" title="Saved">
                ✓
              </span>
            )}
          </div>
        </td>
        )}
      </tr>
      {expanded && (
        <tr className="bg-bg-subtle/40 border-b border-border-subtle">
          <td></td>
          <td colSpan={detailColSpan} className="px-3 py-3">
            <PageDetail page={page} />
          </td>
        </tr>
      )}
    </>
  );
}

function PageDetail({ page }: { page: CrawlPage }) {
  const headingGroups: { label: string; items: string[] | null }[] = [
    { label: "H2", items: page.topH2s },
    { label: "H3", items: page.topH3s },
    { label: "H4", items: page.topH4s },
    { label: "H5", items: page.topH5s },
    { label: "H6", items: page.topH6s },
  ];
  return (
    <div className="text-xs space-y-1.5 text-text-muted">
      <DetailRow label="Full URL">
        <a
          href={page.url}
          target="_blank"
          rel="noreferrer"
          className="text-accent hover:underline break-all"
        >
          {page.url}
        </a>
      </DetailRow>
      <DetailRow label="H1">
        {page.h1 ? (
          <span className="text-text">{page.h1}</span>
        ) : (
          <span className="text-danger font-semibold">(missing)</span>
        )}
      </DetailRow>
      {headingGroups.map((g) =>
        g.items && g.items.length > 0 ? (
          <DetailRow key={g.label} label={`${g.label} (${g.items.length})`}>
            <span className="text-text">{g.items.join(" · ")}</span>
          </DetailRow>
        ) : null
      )}
      <DetailRow label="Meta Description">
        {page.metaDescription ? (
          <span className="text-text">{page.metaDescription}</span>
        ) : (
          <span className="text-text-subtle italic">(none)</span>
        )}
      </DetailRow>
      <DetailRow label="Words">
        <span className="text-text">{page.wordCount?.toLocaleString() ?? "—"}</span>
      </DetailRow>
      <DetailRow label="Internal Links">
        <span className="text-text">
          {page.internalLinkCount?.toLocaleString() ?? "—"}
        </span>
      </DetailRow>
      <DetailRow label="External Links">
        <span className="text-text">
          {page.externalLinkCount?.toLocaleString() ?? "—"}
        </span>
      </DetailRow>
      <DetailRow label="Auto-classified">
        <span className="text-text">{page.autoAssignment ?? "none"}</span>
      </DetailRow>
    </div>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3">
      <span className="text-text-subtle">{label}</span>
      <span>{children}</span>
    </div>
  );
}

// =========================================================================
// Helpers
// =========================================================================
function truncatePath(p: string): string {
  if (p.length <= 38) return p;
  return p.slice(0, 35) + "...";
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "numeric",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
}
